import { Button, DialogContentText } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/styles";
import AnnouncementListPage from "@saleor/announcements/components/AnnouncementListPage";
import AnnouncementListViewPage from "@saleor/announcements/components/AnnouncementListViewPage";
import { TypedAnnouncementBulkDelete } from "@saleor/announcements/mutations";
import { useAnnouncementListQuery } from "@saleor/announcements/queries";
import { AnnouncementBulkDelete } from "@saleor/announcements/types/AnnouncementBulkDelete";
import {
  announcementAddUrl,
  announcementListUrl,
  AnnouncementListUrlDialog,
  AnnouncementListUrlQueryParams,
  announcementUrl
} from "@saleor/announcements/urls";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import {
  AnnouncementSortField,
  OrderDirection,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import React from "react";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filter";

interface AnnouncementListProps {
  params: AnnouncementListUrlQueryParams;
}

const useStyles = makeStyles(
  {
    deleteIcon: {
      marginRight: "8px"
    }
  },
  { name: "BannerList" }
);

export const AnnouncementList: React.FC<AnnouncementListProps> = ({
  params
}) => {
  const navigate = useNavigator();
  const classes = useStyles("");
  const intl = useIntl();
  const notify = useNotifier();
  const paginate = usePaginator();
  const shop = useShop();
  const user = useUser();

  const currencySymbol = shop?.defaultCurrency || "PHP";
  const { updateListSettings, settings } = useListSettings(
    ListViews.ANNOUNCEMENT_LIST
  );

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const sort = {
    sortBy: {
      direction: OrderDirection.DESC,
      field: AnnouncementSortField.PUBLISHING_DATE
    }
  };

  const { data, loading } = useAnnouncementListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      ...sort
    }
  });

  React.useEffect(
    () =>
      navigate(
        announcementListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );
  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: announcementListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    AnnouncementListUrlDialog,
    AnnouncementListUrlQueryParams
  >(navigate, announcementListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      announcementListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(announcementListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.announcements.pageInfo),
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(params);

  const handleAnnouncementBulkDelete = (data: AnnouncementBulkDelete) => {
    if (data.announcementBulkDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Announcement successfully deleted."
        })
      });
      reset();
      closeModal();
    }
  };

  if (
    user.user.userType.code === "" ||
    user.user.userType.code === SmopUserTypeCode.MARKETING
  ) {
    return (
      <TypedAnnouncementBulkDelete onCompleted={handleAnnouncementBulkDelete}>
        {(announcementBulkDelete, annnouncementBulkDeleteOpts) => {
          const onAnnouncementBulkDelete = () =>
            announcementBulkDelete({
              variables: {
                ids: params.ids
              }
            });

          return (
            <>
              <AnnouncementListPage
                brands={null}
                announcements={maybe(
                  () => data.announcements.edges.map(edge => edge.node),
                  []
                )}
                currencySymbol={currencySymbol}
                currentTab={currentTab}
                filterOpts={filterOpts}
                initialSearch={params.query || ""}
                onSearchChange={handleSearchChange}
                onFilterChange={changeFilters}
                onAll={resetFilters}
                onTabChange={handleTabChange}
                onTabDelete={() => openModal("delete-search")}
                onTabSave={() => openModal("save-search")}
                tabs={tabs.map(tab => tab.name)}
                settings={settings}
                onAdd={() => navigate(announcementAddUrl())}
                onRowClick={id => () => navigate(announcementUrl(id))}
                disabled={loading}
                onNextPage={loadNextPage}
                onPreviousPage={loadPreviousPage}
                onUpdateListSettings={updateListSettings}
                pageInfo={pageInfo}
                isChecked={isSelected}
                toggle={toggle}
                toggleAll={toggleAll}
                selected={listElements.length}
                toolbar={
                  <Button
                    color="primary"
                    onClick={() =>
                      openModal("delete", {
                        ids: listElements
                      })
                    }
                  >
                    <DeleteIcon className={classes.deleteIcon} />
                  </Button>
                }
              />
              <ActionDialog
                confirmButtonState={annnouncementBulkDeleteOpts.status}
                onClose={closeModal}
                onConfirm={onAnnouncementBulkDelete}
                open={params.action === "delete"}
                title={intl.formatMessage({
                  defaultMessage: "Delete Announcement",
                  description: "dialog header"
                })}
                confirmButtonLabel="Delete"
                variant="delete"
              >
                <DialogContentText>
                  <FormattedMessage
                    defaultMessage="{counter,plural,one{Are you sure you want to delete this announcement?} other{Are you sure you want to delete {displayQuantity} announcements?}}"
                    description="dialog content"
                    values={{
                      counter: params && params.ids && params.ids.length,
                      displayQuantity: (
                        <strong>
                          {params && params.ids && params.ids.length}
                        </strong>
                      )
                    }}
                  />
                </DialogContentText>
              </ActionDialog>
              <SaveFilterTabDialog
                open={params.action === "save-search"}
                confirmButtonState="default"
                onClose={closeModal}
                onSubmit={handleTabSave}
              />
              <DeleteFilterTabDialog
                open={params.action === "delete-search"}
                confirmButtonState="default"
                onClose={closeModal}
                onSubmit={handleTabDelete}
                tabName={maybe(() => tabs[currentTab - 1].name, "...")}
              />
            </>
          );
        }}
      </TypedAnnouncementBulkDelete>
    );
  } else {
    return (
      <>
        <AnnouncementListViewPage
          brands={null}
          announcements={maybe(
            () => data.announcements.edges.map(edge => edge.node),
            []
          )}
          currencySymbol={currencySymbol}
          currentTab={currentTab}
          filterOpts={filterOpts}
          initialSearch={params.query || ""}
          onSearchChange={handleSearchChange}
          onFilterChange={changeFilters}
          onAll={resetFilters}
          onTabChange={handleTabChange}
          onTabDelete={() => openModal("delete-search")}
          onTabSave={() => openModal("save-search")}
          tabs={tabs.map(tab => tab.name)}
          settings={settings}
          onAdd={() => navigate(announcementAddUrl())}
          onRowClick={id => () => navigate(announcementUrl(id))}
          disabled={loading}
          onNextPage={loadNextPage}
          onPreviousPage={loadPreviousPage}
          onUpdateListSettings={updateListSettings}
          pageInfo={pageInfo}
        />
        <SaveFilterTabDialog
          open={params.action === "save-search"}
          confirmButtonState="default"
          onClose={closeModal}
          onSubmit={handleTabSave}
        />
        <DeleteFilterTabDialog
          open={params.action === "delete-search"}
          confirmButtonState="default"
          onClose={closeModal}
          onSubmit={handleTabDelete}
          tabName={maybe(() => tabs[currentTab - 1].name, "...")}
        />
      </>
    );
  }
};
export default AnnouncementList;
